"use client";

import { ColumnDef } from "@tanstack/react-table";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTableColumnHeader } from "./data-table-column-header";
import { dealTypesRadioButtons, getNumberFormatted } from "@/lib/app";
import { capitalizeFirstLetter } from "@revelate/utils";
import { Button } from "@/components/ui/button";
import { Lock, RefreshCcw } from "lucide-react";
import { format, isBefore, isAfter } from "date-fns";
import { UTCDate } from "@date-fns/utc";
import { DateRange, NestedDeal, NestedToken, UserWithDeals } from "@revelate/types";
import { KeyedMutator } from "swr";
import { ProviderDataViewer } from "./provider-data-viewer";
import { OwnerSelect } from "./owner-select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { updateDeal } from "@/lib/supabase";
import { getDealOwner } from "@revelate/utils";
import { Loader } from "@/components/Loader";

type ColumnParams = {
  syncDealsByProvider: (
    provider: string,
    providerId: string
  ) => Promise<Response>;
  isAPILoading: boolean;
  clickedRow: string | null;
  setClickedRow: (value: string | null) => void;
  mutate: KeyedMutator<NestedDeal[]>;
  users?: UserWithDeals[];
  tokens?: NestedToken[];
};

const getStatus = (
  is_closed: boolean,
  is_won: boolean,
  is_invoiced: boolean,
  is_paid: boolean,
  is_read_only: boolean
) => {
  const statuses: string[] = [];
  if (is_closed) statuses.push("Closed");
  if (is_won) statuses.push("Won");
  if (is_invoiced) statuses.push("Invoiced");
  if (is_paid) statuses.push("Paid");
  if (is_read_only) statuses.push("Read Only");

  if (statuses.length === 0) {
    return "Pending";
  }

  return statuses.join(" » ");
};

const getStatusStep = (
  is_closed: boolean,
  is_won: boolean,
  is_invoiced: boolean,
  is_paid: boolean,
  is_read_only: boolean
) => {
  if (is_read_only) return 5;
  if (is_paid) return 4;
  if (is_invoiced) return 3;
  if (is_won) return 2;
  if (is_closed) return 1;
  return 0;
};

// TODO: Move this into a React Hook
export const getColumns = ({
  syncDealsByProvider,
  isAPILoading,
  clickedRow,
  setClickedRow,
  mutate,
  users,
  tokens,
}: ColumnParams): ColumnDef<NestedDeal>[] => {
  return [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "id",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Deal" />
      ),
      cell: ({ row }) => <div className="w-[80px]">{row.getValue("id")}</div>,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Name" />
      ),
      cell: ({ row }) => {
        const name = row.getValue("name")?.toString() || "";
        return (
          <div className="flex space-x-2">
            <span className="max-w-[500px] truncate font-medium">
              {name.substring(0, 20) + (name.length > 20 ? "..." : "")}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: "owner",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Owner" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex space-x-2">
            <span className="max-w-[500px] truncate font-medium">
              <OwnerSelect
                row={row}
                users={users || []}
                mutate={mutate}
                tokens={tokens}
              />
            </span>
          </div>
        );
      },
      filterFn: (row, _id, value) => {
        return getDealOwner(row.original)
          ? value.includes(getDealOwner(row.original)?.id)
          : false;
      },
    },
    {
      accessorKey: "deal_type",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Deal type" />
      ),
      cell: ({ row }) => {
        const dealType = dealTypesRadioButtons.find(
          (d) => d.value === row.getValue("deal_type")
        );

        if (!dealType) {
          return null;
        }

        return (
          <div className="flex items-center">
            <span>{dealType.label} business</span>
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "value",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Value" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {getNumberFormatted(row.getValue("value"))}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "onboarding_value",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Onboarding Value" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {getNumberFormatted(row.getValue("onboarding_value"))}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "currency_code",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Currency" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("currency_code")}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "provider",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Provider" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {capitalizeFirstLetter(row.getValue("provider"))}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "provider_id",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Provider ID" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("provider_id")}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "pipeline",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Pipeline" />
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      sortingFn: (rowA, rowB) => {
        const statusStepA = getStatusStep(
          rowA.getValue("is_closed"),
          rowA.getValue("is_won"),
          rowA.getValue("is_invoiced"),
          rowA.getValue("is_paid"),
          rowA.getValue("is_read_only")
        );
        const statusStepB = getStatusStep(
          rowB.getValue("is_closed"),
          rowB.getValue("is_won"),
          rowB.getValue("is_invoiced"),
          rowB.getValue("is_paid"),
          rowB.getValue("is_read_only")
        );
        return Math.sign(statusStepA - statusStepB);
      },
      filterFn: (row, _, filterValues) => {
        let match = false;

        if (filterValues.includes("Pending")) {
          match = row.original.is_closed === false || match;
        }

        if (filterValues.includes("Closed")) {
          match = row.original.is_closed === true || match;
        }

        if (filterValues.includes("Won")) {
          match = row.original.is_won === true || match;
        }

        if (filterValues.includes("Invoiced")) {
          match = row.original.is_invoiced === true || match;
        }

        if (filterValues.includes("Paid")) {
          match = row.original.is_paid === true || match;
        }

        if (filterValues.includes("Read Only")) {
          match = row.original.is_read_only === true || match;
        }

        return match;
      },
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {getStatus(
              row.getValue("is_closed"),
              row.getValue("is_won"),
              row.getValue("is_invoiced"),
              row.getValue("is_paid"),
              row.getValue("is_read_only")
            )}
          </div>
        );
      },
      // filterFn: (row, id, value) => {
      //   return value.includes(row.getValue(id));
      // },
    },
    {
      accessorKey: "is_read_only",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Read Only" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("is_read_only") ? "YES" : "NO"}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "is_closed",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Closed" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("is_closed") ? "YES" : "NO"}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "is_won",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Won" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("is_won") ? "YES" : "NO"}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "is_invoiced",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Invoiced" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("is_invoiced") ? "YES" : "NO"}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "is_paid",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Paid" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("is_paid") ? "YES" : "NO"}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "days_to_close",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Days to close" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("days_to_close")}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "closed_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Close date" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {format(new UTCDate(row.getValue("closed_at")), "yyyy-MM-dd")}
          </div>
        );
      },
      filterFn: (row, _id, value: DateRange | undefined) => {
        if (!row.original.closed_at) return false;
        if (!value) return true;
        const afterFromDate = !value.from || isAfter(row.original.closed_at, value.from);
        const beforeToDate = !value.to || isBefore(row.original.closed_at, value.to);
        return afterFromDate && beforeToDate;
      },
    },
    {
      accessorKey: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created at" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {format(
              new UTCDate(row.getValue("created_at")),
              "yyyy-MM-dd HH:mm:ss"
            )}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "updated_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Last updated" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {format(
              new UTCDate(row.getValue("updated_at")),
              "yyyy-MM-dd HH:mm:ss"
            )}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "customer_deal_count",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Customer Deal Count" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("customer_deal_count")}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "contract_length_months",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Contract Length" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("contract_length_months")}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "invoice_length_months",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Invoice Period" />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center">
            {row.getValue("invoice_length_months")}
          </div>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "sync",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Sync" />
      ),
      cell: ({ row }) => (
        <div className="flex items-center">
          {row.getValue("is_read_only") ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  key={row.id}
                  disabled={isAPILoading && clickedRow === row.id}
                  onClick={async () => {
                    await updateDeal(row.getValue("id"), {
                      is_read_only: false,
                    });
                    mutate();
                  }}
                  size="icon"
                  variant={row.getValue("is_read_only") ? "ghost" : "default"}
                >
                  <Lock className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <div className="text-md font-semibold">Sync Disabled</div>
                <p className="text-sm text-muted-foreground">
                  Click to unlock deal and enable sync.
                </p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              key={row.id}
              disabled={isAPILoading && clickedRow === row.id}
              onClick={async () => {
                setClickedRow(row.id);
                await syncDealsByProvider(
                  row.getValue("provider"),
                  row.getValue("provider_id")
                );
                mutate();
                setClickedRow(null);
              }}
              size="icon"
              variant={row.getValue("is_read_only") ? "ghost" : "default"}
            >
              {isAPILoading && clickedRow === row.id ? (
                <Loader size={16} stroke="white" />
              ) : (
                <RefreshCcw className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Source" />
      ),
      id: "provider_data_viewer",
      cell: ({ row }) => <ProviderDataViewer row={row} />,
    },
    // {
    //   id: "actions",
    //   cell: ({ row }) => <DataTableRowActions row={row} />,
    // },
  ];
};
